/**
 * Header control to show logged in users details and image
 * ! Create some test cases
 */
//  Generic imports
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";

//  Motif imports
import {
  MotifTextLink,
  MotifTooltip,
  MotifDropdownPortal,
  MotifAvatar,
  MotifDropdownItem,
} from "@ey-xd/motif-react";
import { MotifIconButton } from "@ey-xd/motif-react/Button";

//  Icons imports
import iconLogout from "../../../assets/images/log-out.svg";
import iconDefaultUser from "../../../assets/images/profile_user_icon.jpg";
import iconGenAI from "../../../assets/images/sparks.svg";

//  Redux imports
import { setUserData } from "../../../redux/userSlice";
import { setLoggedOut } from "../../../redux/authSlice";

//  Internal imports
import "./Header.scss";
import { headerConfig } from "../../../config/headerConfig";
import GraphService from "../../../services/graphService";
import AIGuidance from "../modals/aiGuidance/AIGuidance";

export function Header() {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userAvatarURL, setUserAvatarURL] = useState();
  const [displayAIGuidanceModal, setDisplayAIGuidanceModal] = useState(false);

  const { user } = useSelector((state) => state.user);
  const { instance } = useMsal();
  const dispatch = useDispatch();

  /**
   * Show's logged users card
   */
  const showLoggedInUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  /**
   * Hide users card
   */
  const hideLoggedInUserMenu = () => {
    setShowUserMenu(false);
  };

  /**
   * Hook to get profile photo
   */
  useEffect(() => {
    getAvatarByEmail(user?.username);
    // eslint-disable-next-line
  }, []);

  /**
   * Retrieves the user's avatar based on their email address.
   * If an avatar is available, sets the URL for display; otherwise, uses a default user icon.
   * @param {string} email - The user's email address.
   */
  async function getAvatarByEmail(email) {
    const graphService = new GraphService();
    const userData = await graphService.getUserDetailsByEmail(email);
    dispatch(
      setUserData({
        userData: userData,
      })
    );
    const userId = userData?.value[0]?.id;

    if (userId) {
      const userAvatarBlob = await graphService.getUserAvatar(userId);
      if (userAvatarBlob) {
        const blob = new Blob([userAvatarBlob], { type: "image/jpeg" });
        setUserAvatarURL(URL.createObjectURL(blob));
      } else {
        setUserAvatarURL(iconDefaultUser);
      }
    } else {
      setUserAvatarURL(iconDefaultUser);
    }
  }

  /**
   * Description
   * -----------
   /**
 * Handles user logout by sending a logout request to the authentication instance.
 * @param {Event} e - The event object (e.g., button click event).
   */
  const handleLogOut = (e) => {
    const logoutRequest = {
      account: user,
      postLogoutRedirectUri: "/",
    };
    instance.logout(logoutRequest);
    dispatch(setLoggedOut());
  };

  const platformURL = process.env.REACT_APP_PLATFORMURL;

  /**
   * Open AI Guidance modal
   */
  const openAIGuidance = () => {
    setDisplayAIGuidanceModal(true);
  };

  /**
   * Close AI Guidance modal
   */
  const closeAIGuidance = () => {
    setDisplayAIGuidanceModal(false);
  };

  return (
    <div className="header" id="header">
      <div className="headercontainer">
        <div className="logomenu ">
          <div className="projectTitle">
            <MotifTextLink href={platformURL} target="_blank">
              {headerConfig?.accessPlatformLabel}
            </MotifTextLink>
          </div>
        </div>
        <div className="aiGuidanceContainer">
          <MotifIconButton
            aria-label={headerConfig?.aiGuidanceLabel}
            className="btnTableActions"
            onClick={() => openAIGuidance()}
          >
            <MotifTooltip
              trigger={
                <img
                  src={iconGenAI}
                  alt={headerConfig?.aiGuidanceLabel}
                  className="aiGuidanceImage"
                />
              }
              placement="bottom"
              contentClassName="tooltipWithoutClose"
              allowClick={false}
              hideCloseButton={true}
            >
              <div className="aiguidanceToolTip">
                {headerConfig?.aiGuidanceLabel}
              </div>
            </MotifTooltip>
          </MotifIconButton>
          {displayAIGuidanceModal && (
            <AIGuidance
              openModal={displayAIGuidanceModal}
              setOpenModal={setDisplayAIGuidanceModal}
              closeModal={closeAIGuidance}
            />
          )}
        </div>
        <div className="profileImageContainer">
          <MotifDropdownPortal
            aria-labelledby="dropdown-trigger-3"
            id="ProfileDropdown"
            open={showUserMenu}
            handleClickOutside={() => hideLoggedInUserMenu()}
            className="dropdownContainer"
            placement="bottom"
            hideArrow={true}
            trigger={
              <MotifIconButton
                aria-label="Profile"
                onClick={() => showLoggedInUserMenu()}
                id="divUserAvatar"
                className="Avatar"
              >
                <MotifAvatar
                  description="Profile Icon"
                  src={userAvatarURL}
                  className={showUserMenu ? "userAvatarActive" : "userAvatar"}
                  size="medium"
                />
              </MotifIconButton>
            }
          >
            <MotifDropdownItem onClick={function noRefCheck() {}} disabled>
              <div className="userAvatarUserMenuItem">
                {user?.name}
                <div className="userAvatarEmailMenuItem">{user?.username}</div>
              </div>
            </MotifDropdownItem>
            <MotifDropdownItem
              onClick={(e) => handleLogOut(e)}
              className="logoutcontainer"
            >
              <img className="logoutimg" alt="icon" src={iconLogout} />
              <span className="logout">{headerConfig?.logoutLabel}</span>
            </MotifDropdownItem>
          </MotifDropdownPortal>
        </div>
      </div>
    </div>
  );
}

export default Header;
